import { KaboodleToaster } from "components/common";
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { useGetBackgroundJobsQuery } from "services/background-job-api";
import { useRunAIModelMutation } from "services/metadoc-api";

import { logMetadocDebug } from "../../utils/metadocDebugLogger";

const AI_JOB_TYPE = "AI Sort & Summary";

type UseMetadocAIFlowArgs = {
  fileId?: string;
  metadocId?: string;
  isDev: boolean;
  onAIProcessingComplete: () => void;
};

type MetadocAIPhase = "idle" | "starting" | "running" | "success" | "failed";

type AIFlowState = {
  phase: MetadocAIPhase;
  pendingMetadocId: number | null;
  lastHandledJobId: number | null;
  previousTopAiJobId: number | null;
};

type AIFlowEvent =
  | { type: "METADOC_CHANGED" }
  | { type: "RUN_CLICKED"; metadocId: number; previousTopAiJobId: number }
  | { type: "MUTATION_FAILED" }
  | { type: "JOB_OBSERVED" }
  | { type: "JOB_SUCCEEDED"; jobId: number }
  | { type: "JOB_FAILED"; jobId: number };

const initialAIFlowState: AIFlowState = {
  phase: "idle",
  pendingMetadocId: null,
  lastHandledJobId: null,
  previousTopAiJobId: null,
};

const aiFlowReducer = (state: AIFlowState, event: AIFlowEvent): AIFlowState => {
  switch (event.type) {
    case "METADOC_CHANGED":
      return initialAIFlowState;

    case "RUN_CLICKED":
      return {
        phase: "starting",
        pendingMetadocId: event.metadocId,
        lastHandledJobId: null,
        previousTopAiJobId: event.previousTopAiJobId,
      };

    case "MUTATION_FAILED":
      return {
        ...state,
        phase: "failed",
        pendingMetadocId: null,
      };

    case "JOB_OBSERVED":
      if (state.phase === "success" || state.phase === "failed") {
        return state;
      }
      return {
        ...state,
        phase: "running",
        pendingMetadocId: null,
      };

    case "JOB_SUCCEEDED":
      return {
        phase: "success",
        pendingMetadocId: null,
        lastHandledJobId: event.jobId,
        previousTopAiJobId: state.previousTopAiJobId,
      };

    case "JOB_FAILED":
      return {
        phase: "failed",
        pendingMetadocId: null,
        lastHandledJobId: event.jobId,
        previousTopAiJobId: state.previousTopAiJobId,
      };

    default:
      return state;
  }
};

/*
manages ai-run orchestration and job-state transitions
owns background job polling and terminal-state handling for metadoc ai runs
*/
export const useMetadocAIFlow = ({
  fileId,
  metadocId,
  isDev,
  onAIProcessingComplete,
}: UseMetadocAIFlowArgs) => {
  // sequence 1: define mutation + lifecycle reducer state.
  const [runAIModel, { isLoading: isRunningAIModel, error: aiModelError }] =
    useRunAIModelMutation();
  const [flowState, dispatch] = useReducer(aiFlowReducer, initialAIFlowState);
  const previousPhaseRef = useRef<MetadocAIPhase>(flowState.phase);

  // sequence 2: scope and fetch background jobs for current route context.
  const shouldPollBackgroundJobs =
    isRunningAIModel ||
    flowState.phase === "starting" ||
    flowState.phase === "running";
  const { data: backgroundJobsData, refetch: refetchBackgroundJobs } =
    useGetBackgroundJobsQuery(
      {
        filters: metadocId
          ? { metadoc_id: parseInt(metadocId, 10) }
          : { file_id: fileId },
      },
      {
        skip: !fileId || !metadocId,
        pollingInterval: shouldPollBackgroundJobs ? 3000 : 0,
      },
    );

  // sequence 3: derive ai-relevant job slices from the query payload.
  const currentMetadocIdNum = metadocId ? parseInt(metadocId, 10) : null;
  const jobsForCurrentMetadoc = useMemo(
    () =>
      currentMetadocIdNum && backgroundJobsData?.results
        ? backgroundJobsData.results.filter(
            (job) => job.metadoc_id === currentMetadocIdNum,
          )
        : [],
    [currentMetadocIdNum, backgroundJobsData],
  );
  const aiJobsForCurrentMetadoc = useMemo(
    () => jobsForCurrentMetadoc.filter((job) => job.job_type === AI_JOB_TYPE),
    [jobsForCurrentMetadoc],
  );
  const aiJobsForTrackedRun = useMemo(() => {
    const previousTopAiJobId = flowState.previousTopAiJobId;
    if (
      (flowState.phase === "starting" || flowState.phase === "running") &&
      previousTopAiJobId !== null
    ) {
      return aiJobsForCurrentMetadoc.filter(
        (job) => job.id > previousTopAiJobId,
      );
    }

    return aiJobsForCurrentMetadoc;
  }, [aiJobsForCurrentMetadoc, flowState.phase, flowState.previousTopAiJobId]);
  const hasActiveAIJob = useMemo(
    () =>
      aiJobsForTrackedRun.some(
        (job) => job.status === "Pending" || job.status === "In Progress",
      ),
    [aiJobsForTrackedRun],
  );
  const mostRecentAIJob = aiJobsForTrackedRun[0] ?? null;

  // sequence 4: derive single processing signal used by page transitions.
  const isAwaitingAIJobCreation =
    !!currentMetadocIdNum &&
    flowState.phase === "starting" &&
    flowState.pendingMetadocId === currentMetadocIdNum &&
    aiJobsForTrackedRun.length === 0;
  const isAIProcessing =
    isRunningAIModel ||
    flowState.phase === "starting" ||
    flowState.phase === "running" ||
    hasActiveAIJob ||
    isAwaitingAIJobCreation;

  useEffect(() => {
    if (previousPhaseRef.current === flowState.phase) {
      return;
    }

    logMetadocDebug("ai flow phase changed", {
      metadocId,
      previousPhase: previousPhaseRef.current,
      nextPhase: flowState.phase,
      latestJobStatus: mostRecentAIJob?.status ?? null,
      isDev,
    });

    previousPhaseRef.current = flowState.phase;
  }, [isDev, metadocId, flowState.phase, mostRecentAIJob?.status]);

  // sequence 5: reset lifecycle when the route moves to a different metadoc.
  useEffect(() => {
    logMetadocDebug("ai flow reset for metadoc", { metadocId });
    dispatch({ type: "METADOC_CHANGED" });
  }, [metadocId]);

  // sequence 6: move from starting to running once a live job is visible.
  useEffect(() => {
    if (hasActiveAIJob) {
      logMetadocDebug("ai job observed as active", {
        metadocId,
        activeJobId: mostRecentAIJob?.id ?? null,
        activeJobStatus: mostRecentAIJob?.status ?? null,
      });
      dispatch({ type: "JOB_OBSERVED" });
    }
  }, [hasActiveAIJob, metadocId, mostRecentAIJob?.id, mostRecentAIJob?.status]);

  // sequence 7: run ai model and enter starting phase immediately.
  const handleRunAIModel = useCallback(async () => {
    if (!metadocId) return;
    const metadocIdNum = parseInt(metadocId, 10);

    logMetadocDebug("ai run requested", {
      metadocId: metadocIdNum,
      previousTopAiJobId: aiJobsForCurrentMetadoc[0]?.id ?? 0,
    });
    dispatch({
      type: "RUN_CLICKED",
      metadocId: metadocIdNum,
      previousTopAiJobId: aiJobsForCurrentMetadoc[0]?.id ?? 0,
    });

    try {
      await runAIModel({ metadocId: metadocIdNum }).unwrap();
      logMetadocDebug("ai run mutation accepted", { metadocId: metadocIdNum });
      // request an immediate job refresh; polling remains phase-driven.
      refetchBackgroundJobs();
    } catch (error) {
      logMetadocDebug("ai run mutation failed", {
        metadocId: metadocIdNum,
      });
      dispatch({ type: "MUTATION_FAILED" });
    }
  }, [aiJobsForCurrentMetadoc, metadocId, runAIModel, refetchBackgroundJobs]);

  // sequence 8: handle terminal job statuses exactly once per job id.
  useEffect(() => {
    if (!shouldPollBackgroundJobs || !mostRecentAIJob) {
      return;
    }

    if (flowState.lastHandledJobId === mostRecentAIJob.id) {
      return;
    }

    if (mostRecentAIJob.status === "Success") {
      logMetadocDebug("ai job completed successfully", {
        metadocId,
        jobId: mostRecentAIJob.id,
      });
      dispatch({ type: "JOB_SUCCEEDED", jobId: mostRecentAIJob.id });
      onAIProcessingComplete();
      return;
    }

    if (
      mostRecentAIJob.status === "Failed" ||
      mostRecentAIJob.status === "Error"
    ) {
      logMetadocDebug("ai job failed", {
        metadocId,
        jobId: mostRecentAIJob.id,
        status: mostRecentAIJob.status,
        statusMessage: mostRecentAIJob.status_message ?? null,
      });
      dispatch({ type: "JOB_FAILED", jobId: mostRecentAIJob.id });
      KaboodleToaster.error(
        `AI processing failed: ${mostRecentAIJob.status_message || "Unknown error"}`,
      );
    }
  }, [
    shouldPollBackgroundJobs,
    mostRecentAIJob,
    flowState.lastHandledJobId,
    onAIProcessingComplete,
  ]);

  return {
    aiModelError,
    backgroundJobsData,
    handleRunAIModel,
    isAIProcessing,
    isRunningAIModel,
    refetchBackgroundJobs,
  };
};
